/**
 * Whitespace-only pretty printing and normalisation for pyst queries.
 *
 * Both functions change nothing but whitespace, and only where pyst ignores it —
 * outside string literals, at token boundaries. So `normalize(format(q)) ===
 * normalize(q)` for every query, which is what makes formatting safe to apply to a
 * stored query and what lets the request layer collapse a formatted query back to a
 * single line without changing its meaning.
 */

const INDENT = '  ';
/** A call whose text fits within this on one line is left on one line. */
const WRAP_WIDTH = 80;

/** Index of the closing quote for a string literal starting at `start`. */
function skipString(text: string, start: number): number {
    const quote = text[start];
    let i = start + 1;
    while (i < text.length) {
        const ch = text[i];
        if (quote === "'" && ch === "'") {
            // '' is an escaped quote inside a single-quoted literal.
            if (text[i + 1] === "'") {
                i += 2;
                continue;
            }
            return i;
        }
        if (quote === '"') {
            if (ch === '\\') {
                i += 2;
                continue;
            }
            if (ch === '"') {
                return i;
            }
        }
        i += 1;
    }
    return text.length - 1;
}

/** Split on a separator at nesting depth 0, outside string literals. */
function splitTopLevel(text: string, separator: string): string[] {
    const parts: string[] = [];
    let depth = 0;
    let current = '';
    for (let i = 0; i < text.length; i += 1) {
        const ch = text[i];
        if (ch === "'" || ch === '"') {
            const end = skipString(text, i);
            current += text.slice(i, end + 1);
            i = end;
            continue;
        }
        if (ch === '(' || ch === '[') {
            depth += 1;
        } else if (ch === ')' || ch === ']') {
            depth -= 1;
        }
        if (ch === separator && depth === 0) {
            parts.push(current);
            current = '';
            continue;
        }
        current += ch;
    }
    parts.push(current);
    return parts;
}

/**
 * Collapse a query to canonical single-line form: newlines and runs of whitespace
 * become one space, and spaces vanish around punctuation where pyst never needs
 * them. String literals are untouched.
 */
export function normalizePystWhitespace(text: string): string {
    let out = '';
    let pendingSpace = false;
    const flushSpace = (next: string) => {
        if (!pendingSpace) {
            return;
        }
        pendingSpace = false;
        // No space needed after an opener or separator, or before a closer.
        const prev = out[out.length - 1];
        if (prev === undefined || '(/,=&?['.includes(prev) || ')],/'.includes(next)) {
            return;
        }
        out += ' ';
    };
    for (let i = 0; i < text.length; i += 1) {
        const ch = text[i];
        if (ch === "'" || ch === '"') {
            flushSpace(ch);
            const end = skipString(text, i);
            out += text.slice(i, end + 1);
            i = end;
            continue;
        }
        if (/\s/.test(ch)) {
            pendingSpace = true;
            continue;
        }
        flushSpace(ch);
        out += ch;
    }
    return out.trim();
}

function indentOf(level: number): string {
    return INDENT.repeat(level);
}

/** `name(args)` → parts, or null when the text is not a single call. */
function splitCall(text: string): { name: string; args: string; suffix: string } | null {
    const open = text.indexOf('(');
    if (open <= 0) {
        return null;
    }
    const name = text.slice(0, open);
    if (!/^[~$]?[A-Za-z_][A-Za-z0-9_.]*$/.test(name)) {
        return null;
    }
    // Find the matching close paren, respecting strings.
    let depth = 0;
    for (let i = open; i < text.length; i += 1) {
        const ch = text[i];
        if (ch === "'" || ch === '"') {
            i = skipString(text, i);
            continue;
        }
        if (ch === '(' || ch === '[') {
            depth += 1;
        } else if (ch === ')' || ch === ']') {
            depth -= 1;
            if (depth === 0) {
                return { name, args: text.slice(open + 1, i), suffix: text.slice(i + 1) };
            }
        }
    }
    return null;
}

/**
 * Format one pipeline stage (or any expression) at the given indent. A short call
 * stays on one line; a long one puts each top-level argument on its own line, and an
 * argument that is itself a pipeline (`col_source(x)/col_filter(...)`) is laid out as
 * one, deeper.
 */
function formatExpression(text: string, level: number): string {
    const trimmed = text.trim();

    // A nested pipeline inside an argument: `a(...)/b(...) as alias`.
    const stages = splitTopLevel(trimmed, '/');
    if (stages.length > 1 && stages.every((stage) => stage.trim().length > 0)) {
        return formatPipeline(trimmed, level, false);
    }

    const call = splitCall(trimmed);
    if (!call) {
        return indentOf(level) + trimmed;
    }
    const oneLine = indentOf(level) + trimmed;
    if (oneLine.length <= WRAP_WIDTH) {
        return oneLine;
    }
    const args = splitTopLevel(call.args, ',').map((arg) => arg.trim()).filter((arg) => arg.length);
    if (args.length <= 1) {
        // A single long argument. If it is a bare call or pipeline, lay it out in
        // place; if it is a compound expression (`in(...) and x eq 1`), breaking the
        // inner call open would leave `and x eq 1` dangling after a paren, so keep
        // the whole expression on one indented line instead.
        //
        // A pipeline is not compound: what follows its first call is another stage
        // (`col_source(p)/col_filter(...)/col_select(...) as data`), which reads as
        // one stage per line. Only a suffix that continues the expression itself
        // (` and ...`, ` eq ...`) has to stay put.
        const arg = args[0] ?? '';
        const inner = splitCall(arg);
        const innerSuffix = inner?.suffix.trim() ?? '';
        const compound = innerSuffix.length > 0 && !innerSuffix.startsWith('/');
        const body = arg && !compound ? formatExpression(arg, level + 1) : indentOf(level + 1) + arg;
        return `${indentOf(level)}${call.name}(\n${body}\n${indentOf(level)})${call.suffix}`;
    }
    const body = args.map((arg) => formatExpression(arg, level + 1)).join(',\n');
    return `${indentOf(level)}${call.name}(\n${body}\n${indentOf(level)})${call.suffix}`;
}

/** Stages joined by `/`, one per line. */
function formatPipeline(text: string, level: number, trailingSlashOnLast: boolean): string {
    const stages = splitTopLevel(text, '/').map((stage) => stage.trim()).filter((stage) => stage.length);
    const lines = stages.map((stage) => formatExpression(stage, level));
    return lines.map((line, idx) => (idx < lines.length - 1 || trailingSlashOnLast ? `${line}/` : line)).join('\n');
}

/**
 * Pretty-print a pyst request path or `$apply` pipeline for reading.
 *
 *   /Entity?$param=value&
 *   $apply=
 *     stage(...)/
 *     stage(
 *       arg,
 *       arg
 *     )
 *
 * Macro invocations (`/~name(...)`) get one argument per line.
 */
export function formatPystQuery(query: string): string {
    const flat = normalizePystWhitespace(query);
    if (!flat) {
        return '';
    }

    // Macro path: `/~name($a(1), $b(2))`.
    const macro = /^(\/?~[A-Za-z_][A-Za-z0-9_]*)\((.*)\)$/s.exec(flat);
    if (macro) {
        const args = splitTopLevel(macro[2], ',').map((arg) => arg.trim()).filter((arg) => arg.length);
        if (!args.length) {
            return `${macro[1]}()`;
        }
        return `${macro[1]}(\n${args.map((arg) => indentOf(1) + arg).join(',\n')}\n)`;
    }

    const question = (() => {
        // First `?` outside quotes.
        for (let i = 0; i < flat.length; i += 1) {
            const ch = flat[i];
            if (ch === "'" || ch === '"') {
                i = skipString(flat, i);
                continue;
            }
            if (ch === '?') {
                return i;
            }
        }
        return -1;
    })();

    // No query string: treat the whole thing as an $apply pipeline.
    if (question === -1) {
        return formatPipeline(flat, 0, false);
    }

    const path = flat.slice(0, question);
    const params = splitTopLevel(flat.slice(question + 1), '&');
    const rendered = params.map((param, idx) => {
        const eq = param.indexOf('=');
        const key = eq === -1 ? param : param.slice(0, eq);
        const value = eq === -1 ? '' : param.slice(eq + 1);
        const last = idx === params.length - 1;
        if (key.trim().toLowerCase() === '$apply') {
            return `${key}=\n${formatPipeline(value, 1, false)}${last ? '' : '&'}`;
        }
        return `${key}${eq === -1 ? '' : '=' + value}${last ? '' : '&'}`;
    });
    // First param stays on the path line so the entity and its first option read together.
    const [first, ...rest] = rendered;
    return [`${path}?${first}`, ...rest].join('\n');
}
